import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Input } from './design-system';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { useLocale } from '../contexts/LocaleContext';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { withAlpha } from '../theme';

export interface HttpRequestValue {
  url: string;
  method: 'GET' | 'POST';
  headers: Record<string, string>;
  bodyTemplate?: string;
  responseRootPath?: string;
}

export interface HttpRequestBuilderProps {
  value: HttpRequestValue;
  onChange: (next: HttpRequestValue) => void;
  /** Hide the response-root-path field for consumers that don't parse an array
   * out of the response (e.g. a fire-and-forget action call). Default true. */
  showResponseRootPath?: boolean;
  /** Hide the body-template field even in POST mode, for a consumer with its
   * own body-construction UI. Default true. */
  showBodyTemplate?: boolean;
  urlPlaceholder?: string;
  disabled?: boolean;
}

interface HeaderRow {
  localId: string;
  key: string;
  value: string;
}

let rowIdCounter = 0;
const genRowId = () => `hdr-${Date.now()}-${rowIdCounter++}`;

const rowsFromRecord = (headers: Record<string, string>): HeaderRow[] =>
  Object.entries(headers || {}).map(([key, value]) => ({ localId: genRowId(), key, value }));

const rowsToRecord = (rows: HeaderRow[]): Record<string, string> => {
  const record: Record<string, string> = {};
  for (const row of rows) {
    const key = row.key.trim();
    if (key) record[key] = row.value;
  }
  return record;
};

/**
 * Reusable HTTP request builder — URL, method, headers, optional body
 * template and response-root-path. Controlled (value/onChange), no
 * persistence of its own. Built for the Data Lists webhook config panel
 * (the only place in the app assembling a raw outbound HTTP request today)
 * but deliberately generic — no data-list-specific naming or state — so a
 * future consumer (e.g. a flow-builder "ad-hoc HTTP action" node) can reuse
 * it directly instead of rebuilding a headers editor from scratch.
 */
export const HttpRequestBuilder: React.FC<HttpRequestBuilderProps> = ({
  value,
  onChange,
  showResponseRootPath = true,
  showBodyTemplate = true,
  urlPlaceholder,
  disabled = false,
}) => {
  const styles = useThemedStyles(getStyles);
  const rtl = useRTLStyles();
  const { t } = useLocale();

  // Headers are edited as a stable-keyed row array (not directly off the
  // Record) so typing a new row's key doesn't collide with other empty-key
  // rows mid-edit — converted to/from Record<string,string> only at the
  // value/onChange boundary. Initialized once from the incoming value;
  // parents that reload a different `value.headers` wholesale (e.g.
  // DataListSchemaScreen's loadList()) should remount this component (e.g.
  // via a `key` keyed on the list id) rather than relying on a resync effect.
  const [rows, setRows] = useState<HeaderRow[]>(() => rowsFromRecord(value.headers));

  const updateRows = (next: HeaderRow[]) => {
    setRows(next);
    onChange({ ...value, headers: rowsToRecord(next) });
  };

  const addRow = () => updateRows([...rows, { localId: genRowId(), key: '', value: '' }]);
  const updateRow = (localId: string, patch: Partial<HeaderRow>) =>
    updateRows(rows.map((r) => (r.localId === localId ? { ...r, ...patch } : r)));
  const removeRow = (localId: string) => updateRows(rows.filter((r) => r.localId !== localId));

  return (
    <View>
      <Input
        label={t('httpRequestBuilder.urlLabel')}
        value={value.url}
        onChangeText={(url) => onChange({ ...value, url })}
        placeholder={urlPlaceholder || 'https://example.com/api'}
        autoCapitalize="none"
        editable={!disabled}
      />

      <View style={[styles.methodRow, { flexDirection: rtl.rowDirection }]}>
        {(['GET', 'POST'] as const).map((m) => (
          <TouchableOpacity
            key={m}
            style={[styles.methodPill, value.method === m && styles.methodPillActive]}
            onPress={() => !disabled && onChange({ ...value, method: m })}
            disabled={disabled}
          >
            <Text style={[styles.methodPillText, value.method === m && styles.methodPillTextActive]}>{m}</Text>
          </TouchableOpacity>
        ))}
      </View>

      <Text style={[styles.sectionLabel, { textAlign: rtl.textStart }]}>{t('httpRequestBuilder.headersLabel')}</Text>
      {rows.length === 0 && (
        <Text style={[styles.hint, { textAlign: rtl.textStart }]}>{t('httpRequestBuilder.noHeaders')}</Text>
      )}
      {rows.map((row) => (
        <View key={row.localId} style={[styles.headerRow, { flexDirection: rtl.rowDirection }]}>
          <View style={styles.headerKeyInput}>
            <Input
              value={row.key}
              onChangeText={(key) => updateRow(row.localId, { key })}
              placeholder={t('httpRequestBuilder.headerKeyPlaceholder')}
              autoCapitalize="none"
              editable={!disabled}
            />
          </View>
          <View style={styles.headerValueInput}>
            <Input
              value={row.value}
              onChangeText={(v) => updateRow(row.localId, { value: v })}
              placeholder={t('httpRequestBuilder.headerValuePlaceholder')}
              autoCapitalize="none"
              editable={!disabled}
            />
          </View>
          {!disabled && (
            <TouchableOpacity style={styles.removeButton} onPress={() => removeRow(row.localId)} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
              <Ionicons name="close-circle" size={20} color={colors.textLight} />
            </TouchableOpacity>
          )}
        </View>
      ))}
      {!disabled && (
        <TouchableOpacity style={styles.addHeaderButton} onPress={addRow}>
          <Ionicons name="add" size={16} color={colors.primary} />
          <Text style={styles.addHeaderText}>{t('httpRequestBuilder.addHeader')}</Text>
        </TouchableOpacity>
      )}

      {showBodyTemplate && value.method === 'POST' && (
        <Input
          label={t('httpRequestBuilder.bodyTemplateLabel')}
          value={value.bodyTemplate || ''}
          onChangeText={(bodyTemplate) => onChange({ ...value, bodyTemplate })}
          placeholder={t('httpRequestBuilder.bodyTemplatePlaceholder')}
          autoCapitalize="none"
          multiline
          numberOfLines={4}
          editable={!disabled}
          containerStyle={{ marginTop: spacing.sm }}
        />
      )}

      {showResponseRootPath && (
        <Input
          label={t('httpRequestBuilder.responseRootPathLabel')}
          value={value.responseRootPath || ''}
          onChangeText={(responseRootPath) => onChange({ ...value, responseRootPath })}
          placeholder="data.items"
          autoCapitalize="none"
          editable={!disabled}
          containerStyle={{ marginTop: spacing.sm }}
        />
      )}
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  methodRow: { gap: spacing.xs, marginTop: spacing.sm, marginBottom: spacing.sm },
  methodPill: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
    borderRadius: designTokens.borderRadius.full ?? 999,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  methodPillActive: { backgroundColor: withAlpha(colors.primary, 0.08), borderColor: colors.primary },
  methodPillText: { fontSize: typography.fontSize.sm, color: colors.text.secondary, fontFamily: typography.fontFamily.medium },
  methodPillTextActive: { color: colors.primary, fontFamily: typography.fontFamily.semibold },
  sectionLabel: { fontSize: typography.fontSize.sm, color: colors.text.primary, fontFamily: typography.fontFamily.semibold, marginTop: spacing.sm, marginBottom: spacing.xs },
  hint: { fontSize: typography.fontSize.xs, color: colors.textLight, fontFamily: typography.fontFamily.regular, marginBottom: spacing.xs },
  headerRow: { alignItems: 'center', gap: spacing.xs, marginBottom: spacing.xs },
  headerKeyInput: { flex: 1 },
  headerValueInput: { flex: 1 },
  removeButton: { padding: spacing.xs },
  addHeaderButton: { flexDirection: 'row', alignItems: 'center', gap: 4, alignSelf: 'flex-start', paddingVertical: spacing.xs },
  addHeaderText: { fontSize: typography.fontSize.sm, color: colors.primary, fontFamily: typography.fontFamily.medium },
});
